"use client";

import Link from "@/components/LocaleLink";
import { useSitePathname } from "@/lib/i18n/useSitePathname";
import { partnerHrefs } from "@/lib/usePartnerCode";
import { useActivePartnerCode } from "@/lib/useActivePartnerCode";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "@/lib/i18n/LanguageContext";

/**
 * Top bar carrying a single message. It is intentionally permanent: a dismiss
 * button would need persistence to be meaningful, which this bar does not have.
 */
/**
 * Which language you are advised in, said up front on every screen size
 * (unlike the lead-magnet bar). Nobody on the team speaks Arabic or Ukrainian,
 * and a page in those languages must not suggest otherwise (UWG); Ukrainian
 * readers may also choose Russian. Russian and Vietnamese are real advisory
 * languages (Alexander, Thi Yen), so there the bar says so. Vietnamese runs on
 * WhatsApp: the bookable video call (/beratung) is Alexander's (DE/EN/RU).
 */
const SERVICE_LANGUAGE_NOTE = {
  ar: "ملاحظة: نتواصل معكم ونقدّم الاستشارة بالإنجليزية أو الألمانية.",
  uk: "Зверніть увагу: консультації та листування — англійською, німецькою або російською мовою.",
  // Non-breaking hyphen: "по-" must not end a line on a phone.
  ru: "Консультации на русском языке: Alexander говорит по‑русски.",
  vi: "Tư vấn bằng tiếng Việt qua WhatsApp: Thi\u00A0Yen nói tiếng Việt.",
  // Nobody on the team speaks Spanish (24.10.2026): the bar says so up front,
  // as on the Arabic pages.
  es: "Aviso: asesoramos y respondemos en inglés o alemán.",
} as const;

export function AnnouncementBar() {
  const pathname = useSitePathname();
  const { t, locale } = useTranslation();
  const partnerCode = useActivePartnerCode();

  if (locale === "ar" || locale === "uk" || locale === "ru" || locale === "vi" || locale === "es") {
    return (
      <div className="bg-navy-900">
        <p className="mx-auto max-w-7xl px-4 py-2 text-center text-[12px] font-medium leading-snug text-white/85 sm:px-8 lg:px-10">
          {SERVICE_LANGUAGE_NOTE[locale]}
        </p>
      </div>
    );
  }

  // Checkout, check and inquiry form: the bar links to the check, so there it
  // pointed at the page itself or led away from the form, like the header's
  // red pills, which are hidden there too (visual review 17.10.2026).
  const basePath = (pathname ?? "/").replace(/^\/(en|da|ar|uk|ru|vi|es)(?=\/|$)/, "") || "/";
  if (["/buchen", "/anfrage", "/eignungs-check"].includes(basePath)) return null;

  return (
    // Hidden on mobile, where the vertical space is needed for the hero.
    // Navy, the colour of the header's booking button, so the bar frames the
    // header instead of reading as a second grey strip.
    <div className="hidden bg-navy-900 sm:block">
      <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-2 sm:px-8 lg:px-10">
        <Link
          href={partnerHrefs(partnerCode).check}
          data-track="cta_announcement_checklist"
          className="group inline-flex items-center gap-2 truncate text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85 transition-colors hover:text-white"
        >
          <span suppressHydrationWarning className="truncate">
            {t("announcement", "leadMagnet")}
          </span>
          <ArrowRight className="h-3 w-3 shrink-0 text-coral-300 transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" strokeWidth={2.2} aria-hidden />
        </Link>
      </div>
    </div>
  );
}
